import axios, { type AxiosResponse } from 'axios'
import { v4 as uuidv4 } from 'uuid'
import { PostFormat } from 'sharedKernel/api'
import i18n from 'sharedKernel/i18n/translate/config'
import { toBase64 } from 'sharedKernel/file/handler'
import { regexPatterns } from 'sharedKernel/regex'
import {
  DROPDOWN_ORGAN_TYPE,
  MARCO_LEGAL_OPTIONS,
  OBJETIVOS_OPTIONS,
  VERTICAIS_OPTIONS
} from '../constants'

export const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

export const CREATE_PUBLIC_ADMINISTRATION_URL = 'v1/system/create-public-administration'

const ADDRESS_FIELD_HIDDEN = '-'

const leadershipDeptFallbacks = (): {
  departmentName: string
  creationYear: number
  teamSize: number
} => ({
  departmentName: '',
  creationYear: new Date().getFullYear(),
  teamSize: 0
})

const hiddenRegisterPayloadDefaults = {
  language: 'pt-BR',
  timezone: 'America/Sao_Paulo',
  isSimplifiedResolver: false,
  metWeb: '',
  website: '',
  country: 'Brasil',
  status: 0
} as const

const hiddenAddressPlaceholders = (): {
  name: string
  lineOne: string
  lineTwo: string
  number: string
  zipcode: string
  neighborhood: string
} => ({
  name: ADDRESS_FIELD_HIDDEN,
  lineOne: ADDRESS_FIELD_HIDDEN,
  lineTwo: ADDRESS_FIELD_HIDDEN,
  number: ADDRESS_FIELD_HIDDEN,
  zipcode: ADDRESS_FIELD_HIDDEN,
  neighborhood: ADDRESS_FIELD_HIDDEN
})

const contactPhoneAndMobile = (maskedPhone: string): { phone: string; mobile: string } => {
  const digits = maskedPhone.replace(regexPatterns.clearMaskedNumbers, '')
  if (digits.length === 0) return { phone: '', mobile: '' }
  return { phone: digits, mobile: digits }
}

const administrationTypeIndex = (organTypeValue: string): number => {
  const options = DROPDOWN_ORGAN_TYPE.filter(o => o.value !== '')
  const idx = options.findIndex(o => o.value === organTypeValue)
  return idx >= 0 ? idx : 0
}

const populationSizeToNumber = (value: string): number | undefined => {
  const map: Record<string, number> = {
    pequeno_porte: 0,
    medio_porte: 1,
    grande_porte: 2,
    metropole: 3
  }
  if (!value || !(value in map)) return undefined
  return map[value]
}

const marcoLegalToProcedureIndices = (marcoLegal: Record<string, boolean>): number[] =>
  MARCO_LEGAL_OPTIONS.map((opt, i) => (marcoLegal[opt.id] ? i : -1)).filter(i => i >= 0)

const verticaisToPriorityImpactAreaIndices = (verticais: Record<string, boolean>): number[] =>
  VERTICAIS_OPTIONS.map((opt, i) => (verticais[opt.id] ? i : -1)).filter((v): v is number => v >= 0)

const objetivosToIndices = (objetivos: Record<string, boolean>): number[] =>
  OBJETIVOS_OPTIONS.map((opt, i) => (objetivos[opt.id] ? i : -1)).filter(i => i >= 0)

const parseDateBrToIso = (maskedDate: string): string | undefined => {
  const digits = maskedDate.replace(/\D/g, '')
  if (digits.length !== 8) return undefined
  const d = parseInt(digits.slice(0, 2), 10)
  const m = parseInt(digits.slice(2, 4), 10) - 1
  const y = parseInt(digits.slice(4, 8), 10)
  const date = new Date(y, m, d)
  if (date.getFullYear() !== y || date.getMonth() !== m || date.getDate() !== d) return undefined
  return date.toISOString()
}

export interface ProfileImagePayload {
  content: string
  extension: string
  mimetype: string
  name: string
  size: number
  type: string
}

export const fileToProfileImagePayload = async (file: File): Promise<ProfileImagePayload> => {
  let extension = ''
  if (file instanceof File && typeof file.name === 'string' && file.name.includes('.')) {
    extension = file.name.split('.').pop() ?? ''
  }
  const nameSanitized = file.name.replace(/ /g, '_')
  const result = await toBase64(file)
  let content = ''
  if (typeof result === 'string' && result.includes(',')) {
    content = result.split(',')[1] ?? ''
  } else if (typeof result === 'string') {
    content = result
  }
  if (content === '') {
    throw new Error(i18n.t('Could not read image file'))
  }
  return {
    content,
    extension,
    mimetype: file.type || 'application/octet-stream',
    name: btoa(unescape(encodeURIComponent(nameSanitized))),
    size: file.size,
    type: 'publicAdministrationLogo'
  }
}

export interface BuildPayloadParams {
  focalPoints: Array<{
    id: string
    name: string
    email: string
    phone: string
    cpf: string
    role: string
    linkedin: string
  }>
  institutional: {
    organName: string
    organType: string
    organDescription: string
    cnpj: string
    state: string
    city: string
    populationSize: string
    annualBudget: string
    numberOfServers: string
  }
  leadership: {
    leaderName: string
    mandateUntil: string
    hasInnovationArea: boolean | null
    innovationDeptName: string
    innovationDeptYear: string
    innovationDeptTeamSize: string
    hasIctContract: boolean | null
    ictContractName: string
    marcoLegal: Record<string, boolean>
  }
  intention: {
    verticais: Record<string, boolean>
    objetivos: Record<string, boolean>
    hasCtibudget: boolean | null
    ctibudgetValue: string
    acceptTerms: boolean
  }
  profileImage: ProfileImagePayload | null
}

export const buildCreatePublicAdministrationData = (p: BuildPayloadParams): Record<string, unknown> => {
  const primary = p.focalPoints[0]
  const documentFromCpf = (cpf: string | undefined): string => {
    const d = String(cpf ?? '').replace(/\D/g, '')
    return d.length > 0 ? d : ''
  }

  const linkedinOrEmpty = (s: string): string => (s.trim() === '' ? '' : s.trim())

  const extraFocal = p.focalPoints.slice(1).map(fp => {
    const fpContact = contactPhoneAndMobile(fp.phone)
    const { phone, mobile } = fpContact
    return {
      id: fp.id,
      name: fp.name.trim(),
      contactName: fp.name.trim(),
      contact: {
        phone,
        mobile
      },
      email: fp.email.trim(),
      document: documentFromCpf(fp.cpf),
      position: fp.role.trim(),
      linkedin: linkedinOrEmpty(fp.linkedin)
    }
  })

  const popN = populationSizeToNumber(p.institutional.populationSize)
  const annualBudget = p.institutional.annualBudget ? parseInt(p.institutional.annualBudget, 10) : 0
  const numberEmployers = p.institutional.numberOfServers ? parseInt(p.institutional.numberOfServers, 10) : 0
  const termUntil = parseDateBrToIso(p.leadership.mandateUntil)

  const hasInnovationDepartment = p.leadership.hasInnovationArea === true
  const fb = leadershipDeptFallbacks()

  let departmentName = hasInnovationDepartment
    ? p.leadership.innovationDeptName.trim() || fb.departmentName
    : fb.departmentName
  if (departmentName === '') departmentName = fb.departmentName
  departmentName = typeof departmentName === 'string' ? departmentName.trim() : ''

  let creationYear = hasInnovationDepartment && p.leadership.innovationDeptYear.trim() !== ''
    ? parseInt(p.leadership.innovationDeptYear, 10)
    : fb.creationYear
  if (!Number.isFinite(creationYear)) creationYear = fb.creationYear

  let teamSize = hasInnovationDepartment && p.leadership.innovationDeptTeamSize.trim() !== ''
    ? parseInt(p.leadership.innovationDeptTeamSize, 10)
    : fb.teamSize
  if (!Number.isFinite(teamSize)) teamSize = fb.teamSize

  const leadershipPa = {
    topOfficialName: p.leadership.leaderName.trim(),
    termUntil: termUntil ?? null,
    hasInnovationDepartment,
    departmentName,
    creationYear,
    teamSize,
    hasManagementContract: p.leadership.hasIctContract === true,
    managementContractDescription:
      p.leadership.hasIctContract === true ? p.leadership.ictContractName.trim() || '' : ''
  }

  const proceduresUsed = marcoLegalToProcedureIndices(p.leadership.marcoLegal)
  const innovationProfile = {
    proceduresUsed,
    priorityImpactAreas: verticaisToPriorityImpactAreaIndices(p.intention.verticais),
    programGoals: objetivosToIndices(p.intention.objetivos),
    hasCTIBudget: p.intention.hasCtibudget === true,
    annualCTIBudgetAmount:
      p.intention.hasCtibudget === true && p.intention.ctibudgetValue
        ? parseInt(p.intention.ctibudgetValue, 10)
        : 0
  }

  const address = {
    ...hiddenAddressPlaceholders(),
    city: p.institutional.city.trim() || '',
    state: p.institutional.state.trim() || '',
    country: hiddenRegisterPayloadDefaults.country
  }

  const publicAdministration = {
    address,
    administrationDescription: p.institutional.organDescription.trim(),
    administrationName: p.institutional.organName.trim(),
    administrationType: administrationTypeIndex(p.institutional.organType),
    populationSize: popN !== undefined ? popN : 0,
    numberEmployers: Number.isFinite(numberEmployers) ? numberEmployers : 0,
    cnpj: p.institutional.cnpj.replace(/\D/g, ''),
    estimatedAnnualBudget: Number.isFinite(annualBudget) ? annualBudget : 0,
    innovationProfile,
    leadership: leadershipPa
  }

  const primaryContact = contactPhoneAndMobile(primary.phone)
  const { phone: primaryPhone, mobile: primaryMobile } = primaryContact

  const cnpjDigits = p.institutional.cnpj.replace(/\D/g, '')
  const rootData: Record<string, unknown> = {
    cnpj: cnpjDigits,
    contact: {
      phone: primaryPhone,
      mobile: primaryMobile
    },
    contactName: primary.name.trim(),
    country: hiddenRegisterPayloadDefaults.country,
    document: documentFromCpf(primary?.cpf),
    email: primary.email.trim(),
    focalPoints: extraFocal,
    id: EMPTY_GUID,
    isEcosystemTermsAccepted: p.intention.acceptTerms,
    isSimplifiedResolver: hiddenRegisterPayloadDefaults.isSimplifiedResolver,
    isUserTermsAccepted: p.intention.acceptTerms,
    language: hiddenRegisterPayloadDefaults.language,
    linkedin: linkedinOrEmpty(primary.linkedin),
    metWeb: hiddenRegisterPayloadDefaults.metWeb,
    name: p.institutional.organName.trim(),
    position: primary.role.trim(),
    publicAdministration,
    status: hiddenRegisterPayloadDefaults.status,
    timezone: hiddenRegisterPayloadDefaults.timezone,
    website: hiddenRegisterPayloadDefaults.website
  }
  rootData.profileImage = p.profileImage ?? null
  return rootData
}


export const createPublicAdministrationPost = (params: BuildPayloadParams): PostFormat => ({
  url: CREATE_PUBLIC_ADMINISTRATION_URL,
  isPublic: true,
  data: {
    id: uuidv4(),
    data: buildCreatePublicAdministrationData(params)
  },
  httpConfig: {
    timeout: 120000
  }
})

export const isSuccessfulPublicAdministrationResponse = (
  response: AxiosResponse<unknown>
): boolean => {
  if (response.status < 200 || response.status >= 300) {
    return false
  }
  const data = response.data
  if (data == null || typeof data !== 'object' || Array.isArray(data)) {
    return true
  }
  const d = data as Record<string, unknown>
  if (d.success === false) return false
  if (d.error === true) return false
  if (d.isSuccess === false) return false
  return true
}

const messageForHttpStatus = (status: number): string | null => {
  if (status === 404) {
    return i18n.t('Public administration register service not found')
  }
  if (status === 403) {
    return i18n.t('Public administration register access denied')
  }
  if (status >= 500) {
    return i18n.t('Public administration register server error')
  }
  return null
}

const messageFromResponseData = (status: number | undefined, data: unknown): string | null => {
  if (typeof data === 'string' && data.trim() !== '') {
    return data.trim()
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const d = data as Record<string, unknown>
    if (status === 409) {
      const msg409 = d.message
      if (typeof msg409 === 'string' && msg409.trim() !== '') {
        return msg409.trim()
      }
    }
    for (const key of ['message', 'title', 'errorMessage', 'error', 'detail']) {
      const v = d[key]
      if (typeof v === 'string' && v.trim() !== '') {
        return v.trim()
      }
    }

    const errors = d.errors
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      const msgs: string[] = []
      for (const val of Object.values(errors as Record<string, unknown>)) {
        if (Array.isArray(val)) {
          msgs.push(...val.map(v => String(v)).filter(Boolean))
        } else if (typeof val === 'string' && val.trim() !== '') {
          msgs.push(val.trim())
        }
      }
      if (msgs.length > 0) {
        return msgs.join(' ')
      }
    }
  }

  if (Array.isArray(data) && data.every((x): x is string => typeof x === 'string')) {
    return data.filter(Boolean).join(' ')
  }

  if (status === 409 && (!data || typeof data !== 'object')) {
    return i18n.t('Public administration register conflict')
  }

  return null
}

export const parseRegisterApiError = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error) && error.response) {
    const res = error.response
    const status = res.status
    if (typeof status === 'number') {
      const byStatus = messageForHttpStatus(status)
      if (byStatus) return byStatus
    }

    const fromBody = messageFromResponseData(status, res.data)
    if (fromBody) return fromBody
  }

  if (error instanceof Error && error.message.trim() !== '') {
    const msg = error.message
    if (!msg.startsWith('Request failed') && !msg.startsWith('Network Error')) {
      return msg
    }
  }

  return fallback
}
